import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { DateSettings } from '@/lib/utils/date';
import type { MetaboxUserPreferences } from '@/components/posts/metabox/types';
import type { TablePreferences } from '@/components/admin/data-table/types';
import type { AdminLocale } from '@/lib/i18n/admin-translations';
const packageJson = { version: process.env.NEXT_PUBLIC_APP_VERSION ?? '0.0.0' };

/**
 * Theme cache entry with hash for invalidation
 */
interface ThemeCacheEntry {
  hash: string;
  css: string;
}

/**
 * App Store State Interface
 *
 * Central application state managed by Zustand.
 * Add new state slices here as the application grows.
 */
export interface AppState {
  // Sidebar state
  sidebarCollapsed: boolean;
  sidebarSize: number; // Panel size as percentage
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarSize: (size: number) => void;
  toggleSidebar: () => void;

  // UI preferences
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;

  // Theme CSS cache
  themeCache: ThemeCacheEntry | null;
  setThemeCache: (hash: string, css: string) => void;
  getThemeCSSIfValid: (hash: string) => string | null;
  invalidateThemeCache: () => void;

  // Metabox order/collapse preferences (persisted per post type)
  metaboxPreferences: MetaboxUserPreferences;
  setMetaboxOrder: (postTypeSlug: string, order: readonly string[]) => void;
  toggleMetaboxCollapsed: (postTypeSlug: string, metaboxId: string) => void;

  // Table preferences (persisted per tableId)
  tablePreferences: Record<string, TablePreferences>;
  setTablePreference: (
    tableId: string,
    prefs: Partial<TablePreferences>
  ) => void;
  resetTablePreference: (tableId: string) => void;

  // Sheet panel widths (persisted per sheetId)
  sheetWidths: Record<string, number>;
  setSheetWidth: (sheetId: string, width: number) => void;

  // Admin UI language preference
  adminLocale: AdminLocale;
  setAdminLocale: (locale: AdminLocale) => void;

  // Active header widget sheet (only one open at a time)
  activeWidget: string | null;
  openWidget: (widgetId: string) => void;
  closeWidget: () => void;

  // Server-hydrated settings (not persisted — fresh from server on each layout render)
  dateSettings: DateSettings | null;
  setDateSettings: (settings: DateSettings) => void;

  // AI configuration status (server-hydrated, not persisted)
  aiEnabled: boolean;
  setAiEnabled: (enabled: boolean) => void;
}

/**
 * Generate a simple hash for theme inputs
 */
export function generateThemeHash(
  primaryColor: string,
  borderRadius: string,
  spacingDensity: string,
  fontFamily: string
): string {
  return `${primaryColor}|${borderRadius}|${spacingDensity}|${fontFamily}`;
}

/**
 * Convert semver string (e.g. "0.1.32") to a numeric store version.
 * Uses major*10000 + minor*100 + patch so each bump invalidates stale localStorage.
 */
function parseStoreVersion(semver: string): number {
  const [major = 0, minor = 0, patch = 0] = semver.split('.').map(Number);
  return major * 10000 + minor * 100 + patch;
}

/**
 * App Store with localStorage persistence
 *
 * Uses Zustand's persist middleware to automatically save
 * and restore state from localStorage.
 */
export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Sidebar state
      sidebarCollapsed: false,
      sidebarSize: 13, // Default ~250px at 1920px viewport
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setSidebarSize: (size) => set({ sidebarSize: size }),
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // UI preferences (not persisted - see partialize below)
      commandPaletteOpen: false,
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),

      // Theme CSS cache
      themeCache: null,
      setThemeCache: (hash, css) => set({ themeCache: { hash, css } }),
      getThemeCSSIfValid: (hash) => {
        const cache = get().themeCache;
        if (cache && cache.hash === hash) {
          return cache.css;
        }
        return null;
      },
      invalidateThemeCache: () => set({ themeCache: null }),

      // Metabox preferences
      metaboxPreferences: {},
      setMetaboxOrder: (postTypeSlug, order) =>
        set((state) => ({
          metaboxPreferences: {
            ...state.metaboxPreferences,
            [postTypeSlug]: {
              ...state.metaboxPreferences[postTypeSlug],
              order,
              collapsed:
                state.metaboxPreferences[postTypeSlug]?.collapsed ?? [],
            },
          },
        })),
      toggleMetaboxCollapsed: (postTypeSlug, metaboxId) =>
        set((state) => {
          const entry = state.metaboxPreferences[postTypeSlug];
          const collapsed = entry?.collapsed ?? [];
          const next = collapsed.includes(metaboxId)
            ? collapsed.filter((id) => id !== metaboxId)
            : [...collapsed, metaboxId];
          return {
            metaboxPreferences: {
              ...state.metaboxPreferences,
              [postTypeSlug]: {
                ...entry,
                order: entry?.order ?? [],
                collapsed: next,
              },
            },
          };
        }),

      // Table preferences
      tablePreferences: {},
      setTablePreference: (tableId, prefs) =>
        set((state) => ({
          tablePreferences: {
            ...state.tablePreferences,
            [tableId]: {
              ...state.tablePreferences[tableId],
              ...prefs,
            },
          },
        })),
      resetTablePreference: (tableId) =>
        set((state) => {
          const { [tableId]: _, ...rest } = state.tablePreferences;
          return { tablePreferences: rest };
        }),

      // Sheet panel widths
      sheetWidths: {},
      setSheetWidth: (sheetId, width) =>
        set((state) => ({
          sheetWidths: { ...state.sheetWidths, [sheetId]: width },
        })),

      // Admin UI language preference
      adminLocale: 'en',
      setAdminLocale: (locale) => set({ adminLocale: locale }),

      // Active header widget (only one at a time)
      activeWidget: null,
      openWidget: (widgetId) => set({ activeWidget: widgetId }),
      closeWidget: () => set({ activeWidget: null }),

      // Server-hydrated settings
      dateSettings: null,
      setDateSettings: (settings) => set({ dateSettings: settings }),

      // AI configuration status (server-hydrated)
      aiEnabled: false,
      setAiEnabled: (enabled) => set({ aiEnabled: enabled }),
    }),
    {
      name: 'apollo-app-storage', // localStorage key
      version: parseStoreVersion(packageJson.version),
      migrate: () => ({}), // Reset to defaults on version mismatch
      storage: createJSONStorage(() => localStorage),
      // Persist sidebar and theme cache
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarSize: state.sidebarSize,
        themeCache: state.themeCache,
        metaboxPreferences: state.metaboxPreferences,
        tablePreferences: state.tablePreferences,
        sheetWidths: state.sheetWidths,
        adminLocale: state.adminLocale,
      }),
    }
  )
);
